import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, ShieldCheck, User, Users, X } from 'lucide-react';

import { logoImage } from '../../content/siteContent.mjs';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const NAV = [
  { label: 'Wholesale', to: '/wholesale' },
  { label: 'Menu', to: '/menu' },
  { label: 'Catering', to: '/catering' },
  { label: 'Where to find us', to: '/where-to-find-us' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

const PORTALS = [
  { label: 'Wholesale Client', href: '/client/login', Icon: Users },
  { label: 'Bakery Staff', href: '/staff/login', Icon: User },
  { label: 'Admin', href: '/admin/login', Icon: ShieldCheck },
];

export const PublicHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => setIsMobileMenuOpen(false), [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 gap-4">
          <Link to="/" className="flex-shrink-0" data-testid="public-logo-link">
            <img
              src={logoImage.src}
              alt={logoImage.alt}
              width={logoImage.width}
              height={logoImage.height}
              className="h-14 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-5" aria-label="Main">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`text-sm font-medium transition-colors ${
                  pathname === item.to ? 'text-amber-600' : 'text-gray-700 hover:text-gray-900'
                }`}
                data-testid={`nav-${item.to.replace(/\//g, '') || 'home'}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button asChild className="bg-gray-900 text-white hover:bg-gray-800">
              <Link to="/wholesale#inquiry" data-testid="header-wholesale-cta">
                Wholesale inquiry
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-gray-900 text-gray-900 hover:bg-gray-50"
                  data-testid="portal-login-trigger"
                >
                  Portal Login
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Select Portal</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {PORTALS.map(({ label, href, Icon }) => (
                  <DropdownMenuItem
                    key={href}
                    onClick={() => { window.location.href = href; }}
                    className="cursor-pointer"
                    data-testid={`portal-link-${href.split('/')[1]}`}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    <span>{label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block px-3 py-2.5 rounded-md font-medium text-gray-700 hover:bg-gray-50"
              >
                {item.label}
              </Link>
            ))}
            <div className="border-t pt-3 mt-3 space-y-2">
              <Button asChild className="w-full bg-gray-900 text-white hover:bg-gray-800">
                <Link to="/wholesale#inquiry">Wholesale inquiry</Link>
              </Button>
              <p className="px-3 pt-2 text-sm font-semibold text-gray-500">Portal Login</p>
              {PORTALS.map(({ label, href, Icon }) => (
                <Button key={href} asChild variant="outline" className="w-full justify-start">
                  <a href={href}>
                    <Icon className="mr-2 h-4 w-4" />
                    {label}
                  </a>
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
